'use client';

import { memo, useState, useCallback } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';

const navLinks = [
  { label: 'Home', href: '/homepage' },
  { label: 'About Us', href: '/about' },
  { label: 'Service Areas', href: '/service-areas' },
  { label: 'Gallery', href: '/gallery' },
  { label: 'Testimonials', href: '/testimonials' },
  { label: 'Blog', href: '/blog' },
];

function Navbar() {
  const pathname = usePathname();
  const [mobileOpen, setMobileOpen] = useState(false);

  const toggleMobile = useCallback(() => setMobileOpen(prev => !prev), []);
  const closeMobile = useCallback(() => setMobileOpen(false), []);

  return (
    <nav className="w-full sticky top-0 z-50 shadow-md" style={{ backgroundColor: '#2E7D32' }}>
      <div className="container mx-auto px-6 lg:px-12">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link
            href="/homepage"
            className="flex items-center gap-2 text-white font-bold text-lg hover:opacity-90 transition-opacity"
            onClick={closeMobile}
          >
            <AppLogo className="h-8 w-auto" />
            <span className="hidden sm:block">Gelle Cleaning</span>
          </Link>

          {/* Desktop Nav Links */}
          <div className="hidden md:flex items-center gap-1">
            {navLinks?.map((link) => {
              const isActive = pathname === link?.href;
              return (
                <Link
                  key={link?.href}
                  href={link?.href}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                    isActive
                      ? 'text-white shadow-sm'
                      : 'text-green-100 hover:text-white hover:bg-white/15'
                  }`}
                  style={isActive ? { backgroundColor: '#4CAF50' } : {}}
                >
                  {link?.label}
                </Link>
              );
            })}
          </div>

          {/* Desktop Book Now CTA */}
          <div className="hidden md:flex items-center gap-2">
            <Link
              href="/instant-quote"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all text-white border-2 border-white/30 hover:border-white hover:bg-white/15"
            >
              Instant Quote
            </Link>
            <Link
              href="/homepage#booking"
              className="inline-flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold transition-all shadow-sm text-white border-2 border-white/40 hover:border-white hover:bg-white/15"
              style={{ backgroundColor: '#4CAF50' }}
            >
              Book Now
            </Link>
          </div>

          {/* Mobile Hamburger */}
          <button
            type="button"
            onClick={toggleMobile}
            className="md:hidden p-2 rounded-lg text-white hover:bg-white/15 transition-colors"
            aria-label="Toggle menu"
            aria-expanded={mobileOpen}
          >
            {mobileOpen ? (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
          </button>
        </div>
      </div>
      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="md:hidden border-t border-white/20" style={{ backgroundColor: '#2E7D32' }}>
          <div className="container mx-auto px-6 py-3 flex flex-col gap-1">
            {navLinks?.map((link) => {
              const isActive = pathname === link?.href;
              return (
                <Link
                  key={link?.href}
                  href={link?.href}
                  onClick={closeMobile}
                  className={`px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${
                    isActive
                      ? 'text-white' :'text-green-100 hover:text-white hover:bg-white/15'
                  }`}
                  style={isActive ? { backgroundColor: '#4CAF50' } : {}}
                >
                  {link?.label}
                </Link>
              );
            })}
            <Link
              href="/instant-quote"
              onClick={closeMobile}
              className="px-4 py-3 rounded-lg text-sm font-semibold text-white text-center border-2 border-white/30 hover:border-white hover:bg-white/15 transition-all"
            >
              Instant Quote
            </Link>
            <Link
              href="/homepage#booking"
              onClick={closeMobile}
              className="mt-2 px-4 py-3 rounded-lg text-sm font-semibold text-white text-center border-2 border-white/40 hover:border-white hover:bg-white/15 transition-all"
              style={{ backgroundColor: '#4CAF50' }}
            >
              Book Now
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
}

export default memo(Navbar);
